// src/redux/auth/reducer.ts
// --------------------------------------------------------------------------

import { APICore } from '../../helpers/api/apiCore';
import {
  AuthActionTypes,
  LOGIN_USER_WITH_GOOGLE,
  LOGIN_USER_WITH_GOOGLE_SUCCESS,
  LOGIN_USER_WITH_GOOGLE_FAILURE,
} from './constants';

/* ────────────────────────────────────────────────────────────────────────── */

const api   = new APICore();
const token = localStorage.getItem('token');

const userFromStorage: UserData | null = localStorage.getItem('user')
  ? JSON.parse(localStorage.getItem('user')!)
  : null;

const savedParentCvl = localStorage.getItem('parentCvl');

const getDatabasePrefixByLanguage = (language: string): string => {
  switch (language) {
    case 'spanish':
      return 'dino';
    case 'portuguese':
      return 'dino_uab';
    case 'mexican':
      return 'cfl';
    default:
      return 'dino';
  }
};

const initialLanguage = (userFromStorage as any)?.idioma || 'spanish';

/* ─────────────────────────────────── TYPES ─────────────────────────────── */

export interface UserData {
  id: number;
  username: string;
  password: string;
  firstName: string;
  lastName: string;
  role: string;
  token: string;
  email: string;
  idioma?: string;
}

export interface AuthActionType {
  type:
    | AuthActionTypes.API_RESPONSE_SUCCESS
    | AuthActionTypes.API_RESPONSE_ERROR
    | AuthActionTypes.LOGIN_USER
    | AuthActionTypes.LOGOUT_USER
    | AuthActionTypes.RESET
    | AuthActionTypes.SIGNUP_USER
    | AuthActionTypes.SIGNUP_USER_SUCCESS
    | AuthActionTypes.FORGOT_PASSWORD_REQUEST
    | AuthActionTypes.FORGOT_PASSWORD_SUCCESS
    | AuthActionTypes.FORGOT_PASSWORD_ERROR
    | AuthActionTypes.LOGIN_USER_WITH_GOOGLE
    | AuthActionTypes.LOGIN_USER_WITH_GOOGLE_SUCCESS
    | AuthActionTypes.LOGIN_USER_WITH_GOOGLE_FAILURE
    | AuthActionTypes.RESTORE_SESSION
    | AuthActionTypes.SET_PARENT_CVL
    | AuthActionTypes.UPDATE_DATABASE_PREFIX;
  payload: {
    /* para API_RESPONSE_* */
    actionType?: string;
    data?: UserData | { message?: string };
    error?: string;

    /* para otras acciones */
    parentCvl?: string;
    prefix?: string;
  };
}

export interface AuthState {
  user: UserData | null;
  loading: boolean;
  userLoggedIn: boolean;
  error: string | null;
  trigger: number;
  redirectAfterLogin: boolean;
  currentLanguage: string;
  selectedDatabasePrefix: string;
  parentCvl: string | null;

  /* NUEVOS FLAGS */
  passwordReset: boolean;
  resetPasswordSuccess: string | null;
  userSignUp: boolean;
}

/* ───────────────────────────── INITIAL STATE ──────────────────────────── */

const INIT_STATE: AuthState = {
  user: userFromStorage,
  loading: false,
  userLoggedIn: !!token && !!userFromStorage,
  error: null,
  trigger: 0,
  redirectAfterLogin: false,
  currentLanguage: initialLanguage,
  selectedDatabasePrefix: getDatabasePrefixByLanguage(initialLanguage),
  parentCvl: savedParentCvl,

  /* flags */
  passwordReset: false,
  resetPasswordSuccess: null,
  userSignUp: false,
};

/* ──────────────────────────── REDUCER ──────────────────────────────────── */

const Auth = (state: AuthState = INIT_STATE, action: AuthActionType): AuthState => {
  switch (action.type) {
    /* 0. Petición de “forgot password” iniciada */
    case AuthActionTypes.FORGOT_PASSWORD_REQUEST:
      return { ...state, loading: true, passwordReset: false, resetPasswordSuccess: null };

    /* 1. Cambio de CVL padre */
    case AuthActionTypes.SET_PARENT_CVL: {
      const newCvl = action.payload.parentCvl || null;
      if (newCvl) localStorage.setItem('parentCvl', newCvl);
      else localStorage.removeItem('parentCvl');
      return { ...state, parentCvl: newCvl };
    }

    /* 2. Respuesta EXITOSA genérica */
    case AuthActionTypes.API_RESPONSE_SUCCESS:
      switch (action.payload.actionType) {
        /* 2.1 Login */
        case AuthActionTypes.LOGIN_USER:
        case LOGIN_USER_WITH_GOOGLE_SUCCESS: {
          const userData = action.payload.data as UserData;
          if (userData?.token) {
            localStorage.setItem('token', userData.token);
            localStorage.setItem('user', JSON.stringify(userData));
          }
          const newLang = userData?.idioma || 'spanish';
          return {
            ...state,
            user: userData,
            loading: false,
            userLoggedIn: true,
            trigger: state.trigger + 1,
            redirectAfterLogin: true,
            currentLanguage: newLang,
            selectedDatabasePrefix: getDatabasePrefixByLanguage(newLang),
          };
        }

        /* 2.2 Signup completado */
        case AuthActionTypes.SIGNUP_USER_SUCCESS:
          return { ...state, loading: false, userSignUp: true };

        /* 2.3 Forgot-password completado */
        case AuthActionTypes.FORGOT_PASSWORD_SUCCESS:
          return {
            ...state,
            loading: false,
            passwordReset: true,
            resetPasswordSuccess:
              (action.payload.data as { message?: string })?.message || 'Email sent',
          };

        /* 2.x Otros */
        default:
          return state;
      }

    /* 3. Respuesta de ERROR genérica */
    case AuthActionTypes.API_RESPONSE_ERROR:
      switch (action.payload.actionType) {
        case AuthActionTypes.FORGOT_PASSWORD_ERROR:
          return { ...state, loading: false, error: action.payload.error || 'Error' };
        default:
          return { ...state, loading: false, userLoggedIn: false, error: action.payload.error || null };
      }

    /* 4. Inicio de login */
    case AuthActionTypes.LOGIN_USER:
    case LOGIN_USER_WITH_GOOGLE:
      return { ...state, loading: true, userLoggedIn: false };

    /* 5. Logout explícito */
    case AuthActionTypes.LOGOUT_USER:
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('parentCvl');
      return {
        ...state,
        user: null,
        loading: false,
        userLoggedIn: false,
        currentLanguage: 'spanish',
        selectedDatabasePrefix: getDatabasePrefixByLanguage('spanish'),
        parentCvl: null,
      };

    /* 6. Cambio manual de prefijo BD */
    case AuthActionTypes.UPDATE_DATABASE_PREFIX: {
      const langFromPrefix: Record<string, string> = {
        dino: 'spanish',
        dino_uab: 'portuguese',
        cfl: 'mexican',
      };
      const prefix = action.payload.prefix || state.selectedDatabasePrefix;
      return {
        ...state,
        selectedDatabasePrefix: prefix,
        currentLanguage: langFromPrefix[prefix] || 'spanish',
      };
    }

    /* 7. RESET de Auth */
    case AuthActionTypes.RESET:
      return {
        ...state,
        loading: false,
        error: null,
        userLoggedIn: !!localStorage.getItem('token'),
        redirectAfterLogin: false,
      };

    default:
      return state;
  }
};

export default Auth;
